import { useState, useEffect } from "react";
import "./index.css";
import QuestionManager from "./pages/QuestionManager";
import UploadExcel from "./pages/UploadExcel";
import QuizPage from "./pages/QuizPage";
import RealExamPage from "./pages/RealExamPage";
import AdminLogin from "./pages/AdminLogin";
import { API } from "./api";

export default function App() {
  const [page, setPage] = useState("quiz");
  const [auth, setAuth] = useState({
    authenticated: false,
    is_staff: false,
    username: "",
  });
  const [loadingAuth, setLoadingAuth] = useState(true);

  // Check authentication status on startup (Cloudflare Access admin/status with auth/status fallback)
  useEffect(() => {
    API.get("admin/status/")
      .then((res) => {
        if (res.data && res.data.is_admin) {
          setAuth({
            authenticated: true,
            is_staff: true,
            username: res.data.email || "Admin",
          });
        } else {
          return API.get("auth/status/").then((fallback) => {
            setAuth(fallback.data);
          });
        }
      })
      .catch(() => {
        setAuth({ authenticated: false, is_staff: false, username: "" });
      })
      .finally(() => {
        setLoadingAuth(false);
      });
  }, []);

  const handleLoginSuccess = (userData) => {
    setAuth(userData);
    setPage("questions");
  };

  const handleLogout = async () => {
    try {
      await API.post("auth/logout/");
    } catch (err) {
      console.error("Logout request error", err);
    } finally {
      setAuth({ authenticated: false, is_staff: false, username: "" });
      setPage("quiz");
    }
  };

  const isStaff = auth.authenticated && auth.is_staff;

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar Navigation */}
      <div className="w-64 bg-slate-800 text-white p-6 flex flex-col justify-between shadow-xl">
        <div className="space-y-4">
          <h2 className="text-xl font-bold tracking-tight">📦 Logistics Quiz</h2>

          {/* Public Navigation */}
          <div className="space-y-1">
            <button
              onClick={() => setPage("quiz")}
              className={`block w-full text-left px-3 py-2 rounded transition ${
                page === "quiz" ? "bg-slate-700 font-medium" : "hover:bg-slate-700"
              }`}
            >
              📝 Quiz
            </button>

            <button
              onClick={() => setPage("real_exam")}
              className={`block w-full text-left px-3 py-2 rounded transition ${
                page === "real_exam" ? "bg-slate-700 font-medium" : "hover:bg-slate-700"
              }`}
            >
              🧠 Real Exam
            </button>
          </div>

          {/* Admin Navigation (Staff Only) */}
          {isStaff && (
            <div className="pt-4 border-t border-slate-700 space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-3 block mb-2">
                Staff Management
              </span>

              <button
                onClick={() => setPage("questions")}
                className={`block w-full text-left px-3 py-2 rounded transition ${
                  page === "questions" ? "bg-slate-700 font-medium" : "hover:bg-slate-700"
                }`}
              >
                📋 Manage Questions
              </button>

              <button
                onClick={() => setPage("upload")}
                className={`block w-full text-left px-3 py-2 rounded transition ${
                  page === "upload" ? "bg-slate-700 font-medium" : "hover:bg-slate-700"
                }`}
              >
                📥 Upload Excel
              </button>
            </div>
          )}
        </div>

        {/* Auth / Profile Footer */}
        <div className="pt-4 border-t border-slate-700">
          {loadingAuth ? (
            <div className="text-xs text-slate-400">Loading auth...</div>
          ) : isStaff ? (
            <div className="space-y-2">
              <div className="text-xs text-slate-300">
                Staff: <span className="font-semibold text-white">{auth.username}</span>
              </div>
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2 text-sm text-red-300 hover:bg-slate-700 rounded transition"
              >
                🚪 Sign Out
              </button>
            </div>
          ) : (
            <button
              onClick={() => setPage("login")}
              className={`w-full text-left px-3 py-2 text-sm rounded transition ${
                page === "login" ? "bg-slate-700 text-white" : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              🔒 Staff Login
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-6 overflow-auto">
        {page === "quiz" && <QuizPage />}
        {page === "real_exam" && <RealExamPage />}

        {page === "questions" &&
          (isStaff ? (
            <QuestionManager />
          ) : (
            <AdminLogin
              onLoginSuccess={handleLoginSuccess}
              message="Access restricted: Staff login required to access Question Manager."
            />
          ))}

        {page === "upload" &&
          (isStaff ? (
            <UploadExcel />
          ) : (
            <AdminLogin
              onLoginSuccess={handleLoginSuccess}
              message="Access restricted: Staff login required to upload Excel files."
            />
          ))}

        {page === "login" &&
          (isStaff ? (
            <div className="max-w-md mx-auto mt-16 bg-white p-6 rounded-xl shadow text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Already Signed In
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You are logged in as staff user <b>{auth.username}</b>.
              </p>
              <button
                onClick={() => setPage("questions")}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Go to Question Manager
              </button>
            </div>
          ) : (
            <AdminLogin onLoginSuccess={handleLoginSuccess} />
          ))}
      </div>
    </div>
  );
}